import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import fraudService from "../../services/fraudService";
import { getAccountScanPermissions } from "store/slices/scanPermissionsSlice";

export const getFraudDomains = createAsyncThunk(
    "fraud/domains",
    async ({domainName, createdDate, isExternal}, {rejectWithValue}) => {
        try {
            const response = await fraudService.getFraudDomains(
                domainName,
                createdDate,
                isExternal
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const startFraudScan = createAsyncThunk(
    "fraud/startScan",
    async (data, {rejectWithValue, dispatch}) => {
        try {
            const response = await fraudService.startFraudScan(data);
            if (response.status === 200) {
                    dispatch(getAccountScanPermissions());
                  }
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getDomainFrauds = createAsyncThunk(
    "fraud/getDomainFrauds",
    async (domainId, {rejectWithValue}) => {
        try {
            const response = await fraudService.getDomainFrauds(domainId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getFraudTakeDownStatus = createAsyncThunk("fraud/getFraudTakeDownStatus", async (data, {
    rejectWithValue
}) => {
    try {
        return await fraudService.getFraudTakeDownStatus(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const startFraudTakeDown = createAsyncThunk("fraud/startFraudTakeDown", async (data, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await fraudService.startFraudTakeDown(data);
        response?.data && dispatch(getFraudTakeDownStatus(data?.data?.fraudId));
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getAllFraudTakeDownStatus = createAsyncThunk("fraud/getAllFraudTakeDownStatus", async (data, {
    rejectWithValue
}) => {
    try {
        return await fraudService.getAllFraudTakeDownStatus(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getTakeDownCounts = createAsyncThunk("fraud/getTakeDownCounts", async (data, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await fraudService.getTakeDownCounts();
        if (response.status === 200) {
            dispatch(getAccountScanPermissions());
        }
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});


const initialState = {
    fraudDomains: [],
    domainFrauds: null,
    tableLoading: false,
    fraudTakeDownStatus: null,
    takeDownLoading: false,
    errorMessage: null,
    successMessage: null,
    scanErrorMessage: null,
    scanSuccessMessage: null,
    allFraudTakeDownStatus: null,
    takeDownCounts: null
};

export const fraudSlice = createSlice({
    name: "fraudSlice",
    initialState,
    reducers: {
        resetMessages: (state, action) => {
            state.successMessage = null;
            state.errorMessage = null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getFraudDomains.fulfilled, (state, action) => {
            state.fraudDomains = action.payload;
            state.tableLoading = false;
        });

        builder.addCase(getFraudDomains.pending, (state) => {
            state.tableLoading = true;
        });

        builder.addCase(getFraudDomains.rejected, (state) => {
            state.tableLoading = false;
        });

        builder.addCase(startFraudScan.fulfilled, (state, action) => {
            if(action?.payload?.status === 200 || action?.payload?.status === 204 || action?.payload?.status === 202) {
                state.tableLoading = false;
            } else {
                state.scanErrorMessage = action?.payload?.response?.data?.error || 'error';
                state.tableLoading = false;
            }
        });

        builder.addCase(startFraudScan.pending, (state) => {
            state.tableLoading = true;
            state.scanErrorMessage = null;
            state.scanSuccessMessage = null;
        });

        builder.addCase(startFraudScan.rejected, (state) => {
            state.tableLoading = false;
            state.scanErrorMessage = 'error';
        });
        builder.addCase(getDomainFrauds.fulfilled, (state, action) => {
            state.tableLoading = false;
            state.domainFrauds = action.payload;
        });
        builder.addCase(getDomainFrauds.pending, (state) => {
            state.tableLoading = true;
        });
        builder.addCase(getDomainFrauds.rejected, (state) => {
            state.tableLoading = false;
        });
        //GET FRAUD TAKE DOWN STATUS
        builder.addCase(getFraudTakeDownStatus.fulfilled, (state, action) => {
            state.fraudTakeDownStatus = action.payload.data;
            state.takeDownLoading = false;
        });
        builder.addCase(getFraudTakeDownStatus.pending, (state, action) => {
            state.successMessage = null;
            state.errorMessage = null;
            state.fraudTakeDownStatus = null;
            state.takeDownLoading = true;
        });
        builder.addCase(getFraudTakeDownStatus.rejected, (state, action) => {
            state.errorMessage = 'error';
            state.takeDownLoading = false;
        });
        //START FRAUD TAKE DOWN
        builder.addCase(startFraudTakeDown.fulfilled, (state, action) => {
            if (action.payload?.status === 200) {
                state.successMessage = 'success';
            } else {
                state.errorMessage = action?.payload?.response?.data?.error || 'error';
            }

            state.takeDownLoading = false;
        });
        builder.addCase(startFraudTakeDown.pending, (state, action) => {
            state.successMessage = null;
            state.errorMessage = null;
            state.takeDownLoading = true;
        });
        builder.addCase(startFraudTakeDown.rejected, (state, action) => {
            state.errorMessage = 'error';
            state.takeDownLoading = false;
        });
        //GET ALL FRAUD TAKE DOWN STATUS
        builder.addCase(getAllFraudTakeDownStatus.fulfilled, (state, action) => {
            state.allFraudTakeDownStatus = action.payload.data;
            state.takeDownLoading = false;
        });
        builder.addCase(getAllFraudTakeDownStatus.pending, (state, action) => {
            state.successMessage = null;
            state.errorMessage = null;
            state.allFraudTakeDownStatus = null;
            state.takeDownLoading = true;
        });
        builder.addCase(getAllFraudTakeDownStatus.rejected, (state, action) => {
            state.errorMessage = 'error';
            state.takeDownLoading = false;
        });
        //GET TAKE DOWN COUNTS
        builder.addCase(getTakeDownCounts.fulfilled, (state, action) => {
            state.takeDownCounts = action.payload.data;
        });
        builder.addCase(getTakeDownCounts.pending, (state, action) => {
            state.successMessage = null;
            state.errorMessage = null;
            state.takeDownCounts = null;
        });
        builder.addCase(getTakeDownCounts.rejected, (state, action) => {
            state.errorMessage = 'error';
        });
    },
});

export const {
    resetMessages
} = fraudSlice.actions;

export default fraudSlice.reducer;
